import React, { useState, useEffect } from "react";
import Header from "./Header";
import Sidebar from "./Sidebar";
import { useLocation, useNavigate } from "react-router-dom";
import SideBarItems from "./Sidebar";
import { FaBarsStaggered, FaSun, FaMoon } from "react-icons/fa6";
import { FaShieldAlt } from "react-icons/fa";
import { useAppContext } from "../Context/AppProvider";
import Swal from "sweetalert2";

function Layout({ children, setDarkMode, darkMode, lockLightMode = false, isUserPage = false }) {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isLarge, setIsLarge] = useState(true);
  const { logoutAdmin } = useAppContext();

  // Detect admin routes using a switch-style check
  const adminRoutes = [
    "/admin",
    "/admin/questions",
    "/admin/docs",
    "/admin/settings",
    "/admin/chat-history",
    "/admin/lessons",
  ];
  const isAdminPage = adminRoutes.some(
    (route) =>
      location.pathname === route ||
      (route !== "/admin" && location.pathname.startsWith(route))
  );

  useEffect(() => {
    // Automatically hide sidebar on small screens
    const handleResize = () => {
      setSidebarOpen(window.innerWidth >= 768); // md breakpoint
    };

    handleResize(); // Initial call
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleThemeToggle = () => {
    const newMode = !darkMode;
    localStorage.setItem("theme", newMode ? "dark" : "light");
    setDarkMode(newMode);
  };

  const navigate = useNavigate();

  return isAdminPage ? (
    <div className={`h-screen flex flex-col ${darkMode ? "dark" : ""}`}>
      {!isAdminPage && (
        <Header
          darkMode={darkMode}
          setDarkMode={setDarkMode}
          toggleSidebar={() => setSidebarOpen(!sidebarOpen)}
        />
      )}

      {isAdminPage ? (
        <div className="main-layout">
          {/* Sidebar with Login Page Theme */}
          <div
            className={`relative bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 ${
              isLarge ? "sidebar-large" : "sidebar-small"
            }`}
          >
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-20">
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                  backgroundRepeat: "repeat",
                }}
              ></div>
            </div>

            {/* Sidebar Content */}
            <div className="relative z-10 h-full flex flex-col">
              {/* Logo Section */}
              <div className="p-6 border-b border-white/20">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-lg">
                    <FaShieldAlt className="w-6 h-6 text-white" />
                  </div>
                  {isLarge && (
                    <div className="transition-all duration-300">
                      <h1 className="text-xl font-bold text-white">
                        SmartStart AI
                      </h1>
                      <p className="text-xs text-blue-200">Admin Portal</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Navigation Items */}
              <div className="p-4 flex-1">
                <SideBarItems isLarge={isLarge} />
              </div>
            </div>
          </div>
          <div className="right-main">
            <div className="navbar-main">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setIsLarge(!isLarge)}
                  className="p-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 transition-colors duration-200"
                  aria-label="Toggle sidebar"
                >
                  <FaBarsStaggered className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                </button>
                <button
                  onClick={handleThemeToggle}
                  className="p-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 transition-colors duration-200"
                  aria-label="Toggle theme"
                >
                  {darkMode ? (
                    <FaSun className="w-5 h-5 text-yellow-500" />
                  ) : (
                    <FaMoon className="w-5 h-5 text-gray-600" />
                  )}
                </button>
              </div>

              <div className="flex items-center gap-4">
                <button
                  onClick={async () => {
                    const result = await Swal.fire({
                      title: "Are you sure you want to logout?",
                      text: "You will be redirected to the login page.",
                      icon: "warning",
                      showCancelButton: true,
                      confirmButtonColor: "#d33",
                      cancelButtonColor: "#3085d6",
                      confirmButtonText: "Yes, logout!",
                      cancelButtonText: "Cancel",
                      customClass: {
                        confirmButton: "swal2-confirm-button",
                        cancelButton: "swal2-cancel-button",
                      },
                    });

                    if (result.isConfirmed) {
                      localStorage.clear();
                      await logoutAdmin();
                      navigate("/admin/login");
                    }
                  }}
                  className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors duration-200"
                >
                  Logout
                </button>
              </div>
            </div>
            <div className="main-content">{children}</div>
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-hidden">{children}</div>
      )}
    </div>
  ) : (
    <div className={` flex flex-col ${darkMode ? "dark" : ""}`}>
      <Header
        darkMode={darkMode}
        setDarkMode={setDarkMode}
        toggleSidebar={() => setSidebarOpen(!sidebarOpen)}
        lockLightMode={lockLightMode}
        isUserPage={isUserPage}
      />

      <div className="flex-1 overflow-hidden">{children}</div>
    </div>
  );
}

export default Layout;
